import Styles from './PerformanceInsights.module.scss'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { AvatarWithPlatformIcon } from 'screens/Analytics/components/ExternalIntegrationSelect/AvatarWithPlatformIcon'
import { Check02 } from 'blaze-ui/icons/16'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useInsights } from 'hooks/queries/externalIntegrations/useInsights'
import { useIntegrationInsightsProgress } from './useIntegrationInsightsProgress'
import { useIntegrationLoadingProgress } from './useIntegrationLoadingProgress'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { LoadingMessage } from './LoadingMessage'

export interface IntegrationInsightsProps {
  integration: ExternalIntegration
  brandKitId: number
}

export const IntegrationInsights = ({ integration, brandKitId }: IntegrationInsightsProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const { data: insights = [], isSuccess: fetchedInsights } = useInsights({ externalIntegrationId: integration.id })
  const hasCompletedRef = useRef(false)
  const invalidationIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const invalidateQueries = useCallback(async () => {
    if (!integration) return

    queryClient.invalidateQueries([
      'workspaces',
      workspaceId,
      'externalIntegrations',
      integration.id,
      'channelInsights',
    ])
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKitId, 'strategicRecommendations'])
  }, [integration, workspaceId, brandKitId, queryClient])

  const { data: insightsProgressData, isSuccess: fetchedProgress } = useIntegrationInsightsProgress(integration.id, {
    pollUntilFinal: false,
  })

  // if there are no posts to analyze, or all posts have been analyzed, and the insights status is not processing,
  // then the insights have finished processing
  const hasFinishedProcessingPosts = useMemo(() => {
    const noPostsToAnalyze = !!insightsProgressData?.has_fetched_posts || insightsProgressData?.fetched_posts === 0
    const allPostsAnalyzed =
      !!insightsProgressData?.has_fetched_posts &&
      !!insightsProgressData?.analyzed_posts &&
      insightsProgressData?.analyzed_posts > 0 &&
      insightsProgressData?.analyzed_posts === insightsProgressData?.total_posts

    return (
      (noPostsToAnalyze || allPostsAnalyzed) &&
      !insightsProgressData?.brain_channel_insights_status &&
      !insightsProgressData?.posts_fetching_status
    )
  }, [insightsProgressData])

  const { data: insightsProgress } = useIntegrationInsightsProgress(integration.id, {
    pollUntilFinal: true,
    onComplete: () => {
      // Start polling invalidateQueries every 1s until insights array is not empty
      invalidationIntervalRef.current = setInterval(() => invalidateQueries(), 1000)
    },
    enabled: !!integration.id && (!hasFinishedProcessingPosts || !fetchedProgress),
  })

  const { loadingProgress, loadingLabel } = useIntegrationLoadingProgress(insightsProgress)

  const hasPosts = useMemo(() => {
    return !!insightsProgress?.has_fetched_posts && insightsProgress?.fetched_posts > 0
  }, [insightsProgress?.has_fetched_posts, insightsProgress?.fetched_posts])

  // if insights are finished processing and progress has been fetched, and either there are insights,
  // or there are no posts, or the total posts are greater than the analyzed posts, then:
  // call onComplete, clear the interval and set the hasCompletedRef to true
  useEffect(() => {
    if (
      hasFinishedProcessingPosts &&
      !!fetchedProgress &&
      (insights.length > 0 ||
        !hasPosts ||
        (insightsProgressData?.total_posts &&
          insightsProgressData?.analyzed_posts &&
          insightsProgressData?.total_posts > insightsProgressData?.analyzed_posts))
    ) {
      // Clear interval when insights array is not empty anymore
      if (invalidationIntervalRef.current) {
        clearInterval(invalidationIntervalRef.current)
        invalidationIntervalRef.current = null
      }

      // Call onComplete when insights are available
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true
      }
    }
  }, [
    hasFinishedProcessingPosts,
    insights.length,
    hasPosts,
    insightsProgressData?.total_posts,
    insightsProgressData?.analyzed_posts,
    fetchedProgress,
  ])

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (invalidationIntervalRef.current) {
        clearInterval(invalidationIntervalRef.current)
      }
    }
  }, [])

  const emptyInsights = hasFinishedProcessingPosts && !!fetchedInsights && !insights.length
  const emptyInsightsNotCompleted = emptyInsights && !hasCompletedRef.current
  const emptyInsightsCompleted = emptyInsights && hasCompletedRef.current

  return (
    <div key={integration.id} className={Styles.insightItem}>
      <div className={Styles.header}>
        <div className={Styles.provider}>
          <AvatarWithPlatformIcon size="xs" avatar={integration.picture} platform={integration?.provider} />
          <Heading level={4}>{integration.name}</Heading>
        </div>
        <div className={Styles.check}>
          <div className={Styles.checkIcon}>
            <Check02 size={8} color="var(--light-100)" />
          </div>
          <Text variant="smallList" color="#20A14F">
            Connected
          </Text>
        </div>
      </div>
      {(!hasFinishedProcessingPosts || emptyInsightsNotCompleted) && (
        <LoadingMessage label={loadingLabel} progress={loadingProgress} />
      )}
      {emptyInsightsCompleted && !hasPosts && (
        <div className={Styles.emptyMessage}>
          <Text variant="primary" color="var(--dark-90)">
            We couldn't find any posts to analyze.
          </Text>
        </div>
      )}
      {emptyInsightsCompleted && !!hasPosts && (
        <div className={Styles.emptyMessage}>
          <Text variant="primary" color="var(--dark-90)">
            We couldn't find enough posts to generate insights for this account.
          </Text>
        </div>
      )}
      {hasFinishedProcessingPosts && fetchedInsights && !!insights.length && (
        <div className={Styles.insightData}>
          <div className={Styles.insightDataPositive}>
            <div className={Styles.insightDataHeader}>
              ✅<Heading level={5}>Know what drives results</Heading>
            </div>
            <ul className={Styles.insightDataContent}>
              {insights
                .filter((insight) => insight.sentiment === 'positive')
                .map((insight, index) => (
                  <li key={`${insight.external_integration.id}-${index}`} className={Styles.insightDataContentItem}>
                    <Text variant="secondary" color="var(--dark-90)">
                      {insight.content}
                    </Text>
                  </li>
                ))}
            </ul>
          </div>
          <div className={Styles.insightDataNegative}>
            <div className={Styles.insightDataHeader}>
              ⚠️ <Heading level={5}>Areas for improvement</Heading>
            </div>
            <ul className={Styles.insightDataContent}>
              {insights
                .filter((insight) => insight.sentiment === 'negative')
                .map((insight, index) => (
                  <li key={`${insight.external_integration.id}-${index}`} className={Styles.insightDataContentItem}>
                    <Text variant="secondary" color="var(--dark-90)">
                      {insight.content}
                    </Text>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
